import {
	ReactElement,
	useEffect,
	useState
} from "react";
import {
	Dialog,
	FormattedText,
	Toolbar
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	getUserRequestResponse,
	UserRequestKind,
	UserRequestResponseClientModel,
	UserRequestStatus
} from "client/Api/UserRequests/userRequest";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { setTrackingId } from "client/Misc/utils";
import { AlertManager, useAlerts} from "client/Hooks";

export type UserRequestsResponseDetailsDialogProps = {
	id: number;
	onClose(): void;
};

export const UserRequestsResponseDetailsDialog = (props: UserRequestsResponseDetailsDialogProps): ReactElement => {
	const {
		id,
		onClose,
	} = props;

	const [isInteractingWithTheBackend, setIsInteractingWithTheBackend] = useState<boolean>(true);
	const [details, setDetails] = useState<UserRequestResponseClientModel>();
	const alertManager: AlertManager = useAlerts();

	useEffect(
		() => {
			const load = async (id: number, signal: AbortSignal): Promise<void> => {
				try {
					setIsInteractingWithTheBackend(true);
					const userRequest: UserRequestResponseClientModel = await getUserRequestResponse(id, signal);
					setDetails(userRequest);
					setIsInteractingWithTheBackend(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			load(id, cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager, id, onClose]
	);

	return (
		<Dialog
			trackingId="user-request-response-details-dialog"
			hasBusyIndicator={isInteractingWithTheBackend}
		>
			<Dialog.Header
				title={getDialogTitle(details)}
				onClose={onClose}
			/>
			<Dialog.Body>
				{details && (
					<p>
						<b>{localize("_Note")}:</b> <span {...setTrackingId("note")}>{details.note}</span>
					</p>
				)}

			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="secondary"
						text={localize("_Close")}
						onClick={onClose} trackingId="close"
						responsiveBehavior="hide"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
UserRequestsResponseDetailsDialog.displayName = "UserRequestsReponseDetailsDialog";

function getDialogTitle(details: UserRequestResponseClientModel | undefined): string | ReactElement {
	if (!details) {
		return "...";
	}

	switch (details.item.kind)
	{
		case UserRequestKind.AppAccess:
			return (
				<FormattedText
					text={localize(statusToKeyMap[details.status])}
					thingsToReplace={[
						{ what: "{{AppName}}", filler: details.item.name }
					]}
				/>
			);
		default:
			throw new UnreachableCaseError(details.item.kind);
	}
}

const statusToKeyMap: Record<UserRequestStatus.Accepted | UserRequestStatus.Declined, LocalizationKey> = {
	[UserRequestStatus.Accepted]: "_RequestForTheAppNameAppIsAccepted",
	[UserRequestStatus.Declined]: "_RequestForTheAppNameAppIsDeclined"
};
